import { useSyncExternalStore } from 'react';

/**
 * 【响应式基建，阶段 1】断点常量 + useMediaQuery。
 *
 * 数值与 tokens.css 的 --bp-* 令牌一一对应（CSS 的 @media 无法读自定义属性，
 * 两边只能靠契约测试钉住：见 styles/theme-tokens.test.ts）。全站 JS 侧的
 * 响应式判断一律走这里，不允许散落的 window.innerWidth 快照——innerWidth
 * 不订阅变化，旋转平板/拖拽窗口后会拿着过期值渲染。
 *
 * useSyncExternalStore 的三个参数：subscribe 挂在 MediaQueryList 的 change
 * 事件上；getSnapshot 读当前匹配态；服务器快照恒 false（本项目纯 CSR，
 * 仅为满足签名）。
 */
export const BREAKPOINTS = {
  /** ≥768px：平板及以上（<768 = 手机） */
  tablet: 768,
  /** ≥1024px：笔记本及以上 */
  laptop: 1024,
  /** ≥1440px：桌面（1920/2560/3840 同档） */
  desktop: 1440,
} as const;

export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** 手机（<768px）：表格转卡片、流程全屏分步、画布只读 */
export function useIsPhone(): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.tablet - 1}px)`);
}

/** <1024px（手机+平板）：导航收进汉堡抽屉 */
export function useIsBelowLaptop(): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.laptop - 1}px)`);
}

/** 1024–1439px：侧栏收成图标条 */
export function useIsLaptopRail(): boolean {
  return useMediaQuery(
    `(min-width: ${BREAKPOINTS.laptop}px) and (max-width: ${BREAKPOINTS.desktop - 1}px)`,
  );
}
